import React from 'react';
import PageLayout from '../../components/PageLayout';
import PageHero from '../../components/PageHero';

const BREADCRUMBS = [
  { label: 'Home', to: '/' },
  { label: 'Services', to: '/services/ifys-services' },
  { label: 'Housing & Accommodation' },
];

const CheckItem = ({ children }) => (
  <li className="flex items-start gap-3">
    <span className="flex-shrink-0 mt-1 w-6 h-6 rounded-full bg-white flex items-center justify-center">
      <svg className="w-3.5 h-3.5 text-ifys-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
    </span>
    <span className="text-sm sm:text-base text-white/90 leading-relaxed">{children}</span>
  </li>
);

const HousingAccommodationPage = () => {
  return (
    <PageLayout activeLink="services">
      <PageHero
        title="Housing and Accommodation"
        backgroundImage="/images/service-headers/housing_accommodation%201.jpg"
        breadcrumbs={BREADCRUMBS}
        variant="split"
      />

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-ifys-blue mb-6">
          Housing and Accommodation
        </h2>
      </section>

      {/* ── Long Term Accommodation ── */}
      <section className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h3 className="text-2xl sm:text-3xl font-bold text-ifys-blue mb-6">
            Long Term Accommodation
          </h3>

          {/* Long Term Housing */}
          <div className="mb-10">
            <h4 className="text-xl font-bold text-ifys-blue mb-4">Long Term Housing</h4>
            <div className="space-y-4 text-gray-700 text-base sm:text-lg leading-relaxed">
              <p>
                IFYS manages a number of properties to provide longer term housing, for people who
                are eligible for or already on the Housing Register. The properties are funded by the
                Department of Community Housing and Digital Economy and include a mix of properties in
                the following communities:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-600">
                <li>Studio Units in Caboolture</li>
                <li>Studio units in Redcliffe</li>
                <li>1 &amp; 2 bedroom units in Nambour</li>
                <li>2 x 4 bedroom houses on the Sunshine Coast</li>
              </ul>
            </div>
          </div>

          {/* CMSUS */}
          <div>
            <h4 className="text-xl font-bold text-ifys-blue mb-4">
              Community Managed Studio Units (CMSUS)
            </h4>
            <div className="space-y-4 text-gray-700 text-base sm:text-lg leading-relaxed">
              <p>
                IFYS provides 18 studio units in Caboolture, designed to provide furnished
                accommodation for single people who are eligible for or are already on the Housing
                Register. Depending on the type of housing you are eligible for, along with your
                location preferences and your level of need, your name will be automatically put
                forward for any vacancies IFYS has within the program via the Department.
              </p>
              <p>
                IFYS manages several properties to provide longer term housing, for people who are
                eligible for or already on the Housing Register. The properties are funded by the
                Department of Community Housing and Digital Economy and include:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-600">
                <li>Studio accommodation units in Redcliffe</li>
                <li>Two bedroom units in Nambour</li>
                <li>Four bedroom properties on the Sunshine Coast</li>
                <li>One bedroom units in Nambour</li>
              </ul>
              <p>
                Depending on the type of housing you are eligible for, along with your location
                preferences and your level of need, your name will be automatically put forward for
                any vacancies IFYS has within the program via the Department.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Youth Tenancies ── */}
      <section>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h3 className="text-2xl sm:text-3xl font-bold text-ifys-blue mb-6">Youth Tenancies</h3>
          <div className="space-y-4 text-gray-700 text-base sm:text-lg leading-relaxed">
            <p>
              The Old Church provides integrated accommodation and learning opportunities for young
              people aged 16-25 years on the Sunshine Coast, who are at risk of or experiencing
              homelessness.
            </p>
            <p>
              It is designed for young people who are motivated to engage in education, training and
              employment. Young people will be supported to determine and work towards their goals
              across five service offerings, allowing each young person&rsquo;s journey to be
              tailored to their individual strengths. Our five service offerings include education,
              employment, health &amp; wellbeing, independent living skills and community
              participation &amp; engagement.
            </p>
            <p>
              Young people living at the Old Church are required to sign a lease agreement and as
              such need to be motivated and capable of managing their accommodation with the support
              of our Youth Development Workers.
            </p>
          </div>
          <div className="mt-6 p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
            <p className="text-sm text-gray-700 leading-relaxed">
              If you are in need of transitional accommodation, you can contact the team on{' '}
              <a href="tel:0754383000" className="text-ifys-blue font-semibold hover:underline">07 5438 3000</a>.
            </p>
          </div>
        </div>
      </section>

      {/* ── Homelessness ── */}
      <section className="bg-ifys-blue text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <h3 className="text-2xl sm:text-3xl font-bold mb-6">Homelessness</h3>
          <p className="text-white/85 text-base sm:text-lg leading-relaxed mb-10">
            IFYS is a licensed Community Housing Provider under the National Regulatory System for
            Community Housing, is funded by the Queensland Department of Community Housing and
            Digital Economy and provides assistance through a range of programs.
          </p>

          {/* SHS – Immediate Housing Response */}
          <div className="mb-10">
            <h4 className="text-xl font-bold mb-3">
              Specialist Homelessness Services &ndash; Immediate Housing Response
            </h4>
            <div className="space-y-4 text-white/80 text-base leading-relaxed">
              <p>
                The Immediate Housing Response (IHR) is a Queensland Government initiative under the
                Homelessness Program, designed to support any Queenslander experiencing or at
                imminent risk of homelessness. When all options to retain or secure a home have been
                exhausted &mdash; including temporary supported accommodation and other safe housing
                alternatives &mdash; the IHR provides immediate access to short-term temporary
                accommodation, along with essential support services.
              </p>
              <p>
                Specialist Homelessness Services (SHS) deliver support as part of the IHR, helping
                individuals sustain their temporary accommodation while proactively assisting them in
                transitioning to long-term housing options.
              </p>
            </div>
            <div className="mt-4 p-5 rounded-2xl bg-white/10 border border-white/15">
              <p className="text-sm text-white/90 leading-relaxed">
                For access to these Specialist Homelessness Services, please contact{' '}
                <a href="mailto:ihr@ifys.com.au" className="font-semibold text-white hover:underline">ihr@ifys.com.au</a>{' '}
                or call IFYS at{' '}
                <a href="tel:0754383000" className="font-semibold text-white hover:underline">07 5438 3000</a>.
              </p>
            </div>
          </div>

          {/* SHS – Kawana Hub */}
          <div className="mb-10">
            <h4 className="text-xl font-bold mb-3">
              Specialist Homelessness Services &ndash; Kawana Hub
            </h4>
            <p className="text-white/80 text-base leading-relaxed">
              Kawana Hub Accommodation is an accommodation and support program for individual adults
              aged 18 and over. The program provides temporary accommodation for individuals who are
              experiencing homelessness on the Sunshine Coast. Whilst residing at the program,
              residents are supported to explore and work towards securing a more sustainable housing
              solution. The program is a 60 bed facility in Kawana and works alongside Department of
              Housing and other non-government organisations collaborating to support the service
              user.
            </p>
          </div>

          {/* SHS – Homeless Outreach */}
          <div className="mb-10">
            <h4 className="text-xl font-bold mb-3">
              Specialist Homeless Services &ndash; Homeless Outreach
            </h4>
            <p className="text-white/80 text-base leading-relaxed">
              Homeless Outreach Support provides outreach-based case management to individuals and
              families who are homeless or at risk of homelessness. The focus of this case management
              is to support the transition of the service user through to independent living by
              assisting them to address barriers and issues they may have with their housing, health,
              education, living skills, budgeting, relationships etc. IFYS provides this service in
              both the Sunshine Coast &amp; Gympie regions.
            </p>
            <div className="mt-4 p-5 rounded-2xl bg-white/10 border border-white/15">
              <p className="text-sm text-white/90 leading-relaxed">
                To access these Specialist Homeless Services please contact{' '}
                <a href="mailto:homelessintake@ifys.com.au" className="font-semibold text-white hover:underline">homelessintake@ifys.com.au</a>{' '}
                or call IFYS{' '}
                <a href="tel:0754383000" className="font-semibold text-white hover:underline">07 5438 3000</a>.
              </p>
            </div>
          </div>

          {/* Service Integration Initiative */}
          <div>
            <h4 className="text-xl font-bold mb-3">Service Integration Initiative</h4>
            <div className="space-y-4 text-white/80 text-base leading-relaxed">
              <p>
                Service Integration Initiative is funded under the Department of Housing and Public
                Works.
              </p>
              <p>
                Building state-wide capacity for integrated, front-line responses to people with
                multiple needs who are experiencing or at risk of homelessness. IFYS has Place-Based
                Care Coordination Facilitator covering Sunshine Coast and Gympie regions.
              </p>
              <p className="text-white/90 font-medium">
                Sunshine Coast &amp; Gympie Service Integration Group (SCSIG) Facilitator: Romy Cole |{' '}
                <a href="mailto:SunshineCoastSII@ifys.com.au" className="text-white hover:underline">SunshineCoastSII@ifys.com.au</a>
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
              <div>
                <h5 className="font-bold text-white mb-3">Key Benefits for Households</h5>
                <ul className="space-y-3">
                  <CheckItem>Support to access, engage &amp; navigate the service when experiencing or at risk of homelessness.</CheckItem>
                  <CheckItem>Tailored, coordinated support informed by individuals &amp; families.</CheckItem>
                  <CheckItem>Commitment to sustainable outcomes.</CheckItem>
                </ul>
              </div>
              <div>
                <h5 className="font-bold text-white mb-3">Key Benefits for Service Providers</h5>
                <ul className="space-y-3">
                  <CheckItem>Opportunity to join a place-based alliance and gain access to multi-disciplinary services from across the entire service system.</CheckItem>
                  <CheckItem>Coordinated responses for individuals and families requiring a range of supports with relation to homelessness.</CheckItem>
                  <CheckItem>Framework designed to support sustainable outcomes.</CheckItem>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Crisis Accommodation ── */}
      <section className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h3 className="text-2xl sm:text-3xl font-bold text-ifys-blue mb-8">
            Crisis Accommodation
          </h3>

          {/* Youth Shelters */}
          <div className="mb-10">
            <h4 className="text-xl font-bold text-ifys-blue mb-4">Youth Shelters</h4>
            <div className="space-y-4 text-gray-700 text-base sm:text-lg leading-relaxed">
              <p>
                IFYS currently operates two supported crisis accommodation services for young people
                in Caboolture and on the Sunshine Coast. Our youth shelters offer crisis
                accommodation for single young people aged 16-25yrs who are homeless or at risk of
                homelessness. Whilst residing at the shelter, young people will be supported to
                achieve their personal goals and work towards finding and maintaining safe and stable
                housing options.
              </p>
              <p>
                Our youth crisis accommodation services perform a significant role in meeting the
                protective needs of young people with histories of relationship stress and family
                violence, abuse and neglect, substance misuse, mental health and/or behavioural
                difficulties and associated concerns.
              </p>
            </div>
            <div className="mt-4 p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
              <p className="text-sm text-gray-700 leading-relaxed">
                If you are in need of crisis accommodation, you can contact the team on{' '}
                <a href="tel:0754383000" className="text-ifys-blue font-semibold hover:underline">07 5438 3000</a>{' '}
                or call the Homeless Hotline on{' '}
                <a href="tel:1800474753" className="text-ifys-blue font-semibold hover:underline">1800 474 753</a>.
              </p>
            </div>
          </div>

          {/* Same House Different Landlord */}
          <div className="mb-10">
            <h4 className="text-xl font-bold text-ifys-blue mb-4">Same House Different Landlord</h4>
            <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
              IFYS manages and supports young people, up to the age of 25, who are currently on or
              eligible for the Housing Register, in three properties owned by the Department of
              Community Housing and Digital Economy. This program is designed specifically for Service
              Users from within our Crisis Accommodation Program, to transfer into these properties
              with the aim of eventually transitioning to the Department&rsquo;s Public Housing
              Program.
            </p>
            <div className="mt-4 p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
              <p className="text-sm text-gray-700 leading-relaxed">
                If you need short term accommodation you contact the team on{' '}
                <a href="tel:0754383000" className="text-ifys-blue font-semibold hover:underline">07 5438 3000</a>{' '}
                or call the Homelessness Hotline on{' '}
                <a href="tel:1800474753" className="text-ifys-blue font-semibold hover:underline">1800 474 753</a>.
              </p>
            </div>
          </div>

          {/* Crisis Accommodation Program */}
          <div>
            <h4 className="text-xl font-bold text-ifys-blue mb-4">Crisis Accommodation Program</h4>
            <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-4">
              IFYS provides the following units of accommodation within the Crisis Accommodation
              Program:
            </p>

            <div className="space-y-6">
              <div className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
                <p className="text-gray-700 text-base leading-relaxed">
                  A number of properties in the Moreton Region funded specifically to support young
                  people and young families aged 16 to 25 years who are homeless, or at risk of
                  homelessness. The aim is to provide short term accommodation while providing home
                  visits and collaborative case management to work with young people to obtain and
                  maintain safe, secure and affordable accommodation.
                </p>
                <p className="text-sm text-gray-700 mt-3 leading-relaxed">
                  If you need short term accommodation you can contact the team on{' '}
                  <a href="tel:0754383000" className="text-ifys-blue font-semibold hover:underline">07 5438 3000</a>{' '}
                  or call the Homelessness Hotline on{' '}
                  <a href="tel:1800474753" className="text-ifys-blue font-semibold hover:underline">1800 474 753</a>.
                </p>
              </div>

              <div className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
                <p className="text-gray-700 text-base leading-relaxed">
                  A number of properties in partnership with the Salvation Army Caboolture. The IFYS
                  Property Management Team manages the properties and The Salvation Army&rsquo;s
                  Supported Accommodation Caboolture Program provides person centred support to
                  families who are homeless or at risk of homelessness.
                </p>
                <p className="text-gray-700 text-base leading-relaxed mt-3">
                  Families and individuals who are homeless, are eligible for the service and are
                  prioritised for next highest-need basis and best fit for the property.
                </p>
                <p className="text-sm text-gray-700 mt-3 leading-relaxed">
                  If you would like to discuss an assessment, please call the Salvation Army Upfront
                  Intervention Team on{' '}
                  <a href="tel:0737954400" className="text-ifys-blue font-semibold hover:underline">07 3795 4400</a>,
                  or if you would like to meet someone face to face, please contact the Salvation
                  Army direct on{' '}
                  <a href="tel:0754953700" className="text-ifys-blue font-semibold hover:underline">07 5495 3700</a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Advance to Zero ── */}
      <section>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h3 className="text-2xl sm:text-3xl font-bold text-ifys-blue mb-6">Advance to Zero</h3>
          <h4 className="text-xl font-bold text-ifys-blue mb-4">
            Sunshine Coast Zero and Wide Bay Zero
          </h4>
          <div className="space-y-4 text-gray-700 text-base sm:text-lg leading-relaxed">
            <p>
              As part of our commitment to ending homelessness, IFYS is proudly the backbone
              organisation for Advance to Zero (AtoZ) initiatives for the Sunshine Coast and Wide Bay
              regions. These localised efforts are part of a broader national movement led by the
              Australian Alliance to End Homelessness (AAEH), using the internationally recognised
              Built for Zero model to achieve what&rsquo;s known as Functional Zero homelessness.
              This means ensuring homelessness in our communities is rare, brief, and non-recurring.
            </p>
            <p>
              Through AtoZ, IFYS collaborates with community stakeholders, health providers, local
              government, housing agencies, and people with lived experience of homelessness. At the
              heart of our approach is the creation and maintenance of a real-time, By-Name List
              &ndash; a dynamic register of every individual experiencing homelessness. This tool
              enables targeted interventions, service coordination and outcome tracking to ensure no
              one is left behind.
            </p>
          </div>
          <div className="mt-6">
            <a
              href="https://scwbzero.com.au/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-ifys-blue font-semibold hover:underline focus:outline-none focus:ring-2 focus:ring-ifys-blue focus:ring-offset-2 rounded"
            >
              Learn More Here
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
            </a>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default HousingAccommodationPage;
